// Saved filters on Search (Kareem, 2 Nov): personal. "Save this search" keeps the words, the kind and the tags for
// you; the list shows only yours, each with how many results it has now; rename and delete on desktop and phone.
// Nothing is shared: a copied link carries the search itself, and whoever opens it sees only what they can open.
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SavedSearch, SearchKind } from '@dpw/shared';
import { api } from '../api';
import { useChange } from '../lib/queries';
import { listItem, spring } from '../lib/motion';
import { ErrorNote } from '../ui/bits';
import { useToast } from '../ui/toast';
import { P, PSheet, Section } from '../phone/kit';

const KEY = ['search-saved'];
/** Dots in turn (never violet: violet is the assistant's). */
const DOTS = ['red', 'amb', 'grn', 'neu'] as const;

export const useSaved = () => useQuery({ queryKey: KEY, queryFn: api.search.saved, staleTime: 30_000 });

export interface SearchNow { q: string; kind: SearchKind | undefined; tags: string[] }
const same = (f: SavedSearch, s: SearchNow) =>
  f.q.toLowerCase() === s.q.toLowerCase() && (f.kind ?? undefined) === s.kind && [...f.tags].sort().join(',') === [...s.tags].map(t => t.toLowerCase()).sort().join(',');

function useSavedChanges() {
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });
  const save = useChange((s: SearchNow & { name?: string }) => api.search.save({ name: s.name, q: s.q, kind: s.kind ?? null, tags: s.tags }));
  const rename = useChange((x: { id: string; name: string }) => api.search.renameSaved(x.id, x.name));
  const remove = useChange((f: SavedSearch) => api.search.deleteSaved(f.id));
  return {
    save: (s: SearchNow) => save.mutate(s, { onSuccess: f => { void refresh(); toast(`Saved as "${f.name}". Only you see it.`); } }),
    saving: save.isPending, saveError: save.error,
    rename: (id: string, name: string, done: () => void) => rename.mutate({ id, name }, { onSuccess: () => { void refresh(); done(); } }),
    renameError: rename.error,
    remove: (f: SavedSearch, done?: () => void) => remove.mutate(f, {
      onSuccess: () => {
        void refresh(); done?.();
        toast(`Deleted "${f.name}"`, { label: 'Undo', run: () => save.mutate({ name: f.name, q: f.q, kind: f.kind ?? undefined, tags: f.tags }, { onSuccess: () => void refresh() }) });
      },
    }),
  };
}

/** Desktop: the button by the kind switch. Shows Saved when this exact search is already one of yours. */
export function SaveSearchButton({ now }: { now: SearchNow }) {
  const saved = useSaved().data ?? [];
  const c = useSavedChanges();
  if (now.q.length < 2) return null;
  const already = saved.find(f => same(f, now));
  return (
    <>
      <button type="button" className="bt" disabled={!!already || c.saving} onClick={() => c.save(now)}>{already ? 'Saved' : 'Save this search'}</button>
      {c.saveError ? <ErrorNote error={c.saveError} /> : null}
    </>
  );
}

/** Desktop: Saved filters (SSearch, right column), each a link with its live count; Rename and Delete in the row. */
export function SavedPanel({ now }: { now: SearchNow }) {
  const q = useSaved();
  const c = useSavedChanges();
  const [editing, setEditing] = useState<string | null>(null);
  const list = q.data ?? [];
  return (
    <section className="panel svf" aria-label="Saved filters">
      <div className="sh" style={{ marginBottom: 6 }}><b>Saved filters</b><span>Only you see these</span></div>
      {q.error ? <ErrorNote error={q.error} /> : null}
      {q.data && !list.length && <div className="xs mut">Search, then Save this search to keep the words, the kind and the tags here.</div>}
      <ul className="svlist" aria-label="Saved filters">
        <AnimatePresence initial={false}>
          {list.map((f, i) => (
            <motion.li key={f.id} className={`lrow svrow${same(f, now) ? ' on' : ''}`} data-saved={f.name} variants={listItem} initial="initial" animate="animate" exit="exit" layout="position" transition={spring.gentle}>
              {editing === f.id ? (
                <RenameForm f={f} onDone={() => setEditing(null)} rename={c.rename} />
              ) : <>
                <span className={`dt ${DOTS[i % DOTS.length]}`} style={{ marginTop: 0 }} aria-hidden="true" />
                <Link className="sm svname" to={f.href} aria-current={same(f, now) ? 'page' : undefined}>{f.name}</Link>
                <span className="xs mut mono" aria-label={`${f.count} ${f.count === 1 ? 'result' : 'results'}`}>{f.count}</span>
                <span className="svacts">
                  <button type="button" className="bt ghost xs" onClick={() => setEditing(f.id)} aria-label={`Rename ${f.name}`}>Rename</button>
                  <button type="button" className="bt ghost xs" onClick={() => c.remove(f)} aria-label={`Delete ${f.name}`}>Delete</button>
                </span>
              </>}
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
      {c.renameError ? <ErrorNote error={c.renameError} /> : null}
    </section>
  );
}

function RenameForm({ f, onDone, rename }: { f: SavedSearch; onDone: () => void; rename: (id: string, name: string, done: () => void) => void }) {
  const [name, setName] = useState(f.name);
  const submit = (e: FormEvent) => { e.preventDefault(); if (name.trim()) rename(f.id, name, onDone); };
  return (
    <form className="rowc svren" onSubmit={submit} aria-label={`Rename ${f.name}`}>
      <input className="inp" value={name} onChange={e => setName(e.target.value)} aria-label="Name" maxLength={80} autoFocus onKeyDown={e => { if (e.key === 'Escape') onDone(); }} />
      <button className="bt pri sm" disabled={!name.trim()}>Save</button>
      <button type="button" className="bt sm" onClick={onDone}>Cancel</button>
    </form>
  );
}

/** Phone, before you type: Saved filters, each with its count; the dots open a sheet to rename or delete it. */
export function PhoneSavedList() {
  const list = useSaved().data ?? [];
  const [open, setOpen] = useState<SavedSearch | null>(null);
  if (!list.length) return null;
  return (
    <>
      <Section title="Saved filters" count={list.length} />
      <div className="ph-grp flat" role="list" aria-label="Saved filters">
        {list.map(f => (
          <div key={f.id} className="ph-row svprow" role="listitem" data-saved={f.name}>
            <Link className="ph-rt svplink" to={f.href}><div className="t">{f.name}</div><div className="s">{f.count} {f.count === 1 ? 'result' : 'results'}</div></Link>
            <button type="button" className="svpmore" onClick={() => setOpen(f)} aria-label={`Rename or delete ${f.name}`}><P.more /></button>
          </div>
        ))}
      </div>
      <AnimatePresence>{open && <PhoneSavedSheet key={open.id} f={open} onClose={() => setOpen(null)} />}</AnimatePresence>
    </>
  );
}

function PhoneSavedSheet({ f, onClose }: { f: SavedSearch; onClose: () => void }) {
  const c = useSavedChanges();
  const [name, setName] = useState(f.name);
  return (
    <PSheet label={`Saved filter ${f.name}`} onClose={onClose}>
      <h2>{f.name}</h2>
      <form className="col" style={{ gap: 10 }} onSubmit={e => { e.preventDefault(); if (name.trim()) c.rename(f.id, name, onClose); }}>
        <input className="ph-inp" value={name} onChange={e => setName(e.target.value)} aria-label="Name" maxLength={80} />
        <button className="ph-btf" disabled={!name.trim() || name.trim() === f.name}>Rename</button>
        <button type="button" className="ph-btf sec" onClick={() => c.remove(f, onClose)}>Delete</button>
        {c.renameError ? <ErrorNote error={c.renameError} /> : null}
      </form>
    </PSheet>
  );
}

/** Phone, with words typed: Save this search (or Saved, when it is one of yours). */
export function PhoneSaveRow({ now }: { now: SearchNow }) {
  const saved = useSaved().data ?? [];
  const c = useSavedChanges();
  if (now.q.length < 2) return null;
  const already = saved.some(f => same(f, now));
  return (
    <>
      <button type="button" className="ph-row svsave" disabled={already || c.saving} onClick={() => c.save(now)}>
        <span className="ph-ic">{already ? <P.check /> : <P.plus />}</span><span className="ph-rt"><div className="t">{already ? 'Saved' : 'Save this search'}</div></span>
      </button>
      {c.saveError ? <ErrorNote error={c.saveError} /> : null}
    </>
  );
}
